import { useEffect, useState } from "react";
import api, { getLevel } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Copy, CheckCircle, AlertCircle, ExternalLink, RotateCw, X } from "lucide-react";

const STEPS = [
  { n: 1, label: "Open your Google Sheet" },
  { n: 2, label: "Open Apps Script" },
  { n: 3, label: "Paste the code" },
  { n: 4, label: "Install trigger" },
  { n: 5, label: "Deploy as Web App" },
  { n: 6, label: "Paste deploy URL here" },
];

export default function SheetSyncPanel() {
  const isLeadership = getLevel() === "leadership";
  const [cfg, setCfg] = useState(null);
  const [script, setScript] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [webappUrl, setWebappUrl] = useState("");
  const [secretOverride, setSecretOverride] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/sheet/config"),
        api.get("/admin/sheet/apps-script"),
      ]);
      setCfg(c.data);
      setScript(s.data);
      if (c.data?.webapp_url) setWebappUrl(c.data.webapp_url);
    } catch (e) {
      toast.error("Could not load sheet sync settings");
    }
  };
  useEffect(() => { load(); }, []);

  const saveUrl = async () => {
    if (!webappUrl.startsWith("https://script.google.com/")) {
      toast.error("That doesn't look like an Apps Script deploy URL");
      return;
    }
    setSaving(true);
    try {
      // If the user pasted a custom secret (because the script's hardcoded
      // SHARED_SECRET differs from what we generated), honor it. Otherwise use
      // whatever the backend already has (or auto-generates).
      const secret = secretOverride.trim() || script?.shared_secret;
      await api.post("/admin/sheet/config", {
        webapp_url: webappUrl,
        shared_secret: secret,
      });
      toast.success("Sheet connected");
      await load();
      setShowSetup(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const bootstrap = async () => {
    setBootstrapping(true);
    try {
      const r = await api.post("/admin/sheet/bootstrap");
      toast.success(`Pushed ${r.data.rows_pushed} rows to the sheet`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bootstrap failed");
    } finally { setBootstrapping(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect the Google Sheet? The sheet content is preserved but live sync stops.")) return;
    try {
      await api.delete("/admin/sheet/config");
      toast.success("Disconnected");
      setWebappUrl("");
      await load();
    } catch (e) { toast.error("Disconnect failed"); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  if (!cfg) return null;

  return (
    <section className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 lg:col-span-2" data-testid="sheet-sync-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <FileSpreadsheet className="w-6 h-6 text-emerald-700 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h2 className="font-display text-lg font-bold flex items-center gap-2 flex-wrap">
              Google Sheet Payroll Sync
              <span
                className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded font-bold ${
                  cfg.configured ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"
                }`}
                data-testid="sheet-sync-status"
              >
                {cfg.configured ? "CONNECTED" : "NOT CONFIGURED"}
              </span>
            </h2>
            <p className="text-sm text-slate-700 mt-1 max-w-2xl">
              {cfg.configured
                ? "Edits made in the connected Google Sheet flow into the app in real time, and PayrollPanel changes push to the sheet automatically. This sheet is the authoritative payroll report."
                : "Two-way live sync between PayrollPanel and a branded Google Sheet. Replaces the XLSX download as the canonical report. Leadership-only setup."}
            </p>
            {cfg.configured && cfg.last_push_at_utc && (
              <div className="text-xs text-slate-500 mt-2 space-x-3">
                <span>Last sync: {new Date(cfg.last_push_at_utc).toLocaleString()}</span>
                <span>Status: <code className="px-1 py-0.5 rounded bg-white border border-slate-200">{cfg.last_push_status}</code></span>
              </div>
            )}
            {cfg.configured && (
              <div className="mt-3 text-xs text-slate-600 bg-white border border-emerald-200 rounded-lg p-3 max-w-2xl">
                <div className="font-bold mb-1 flex items-center gap-2">
                  Shared secret
                  <button
                    onClick={() => setShowSecret(s => !s)}
                    className="text-emerald-700 underline text-xs font-normal"
                  >
                    {showSecret ? "Hide" : "Show"}
                  </button>
                </div>
                {showSecret && (
                  <div className="font-mono break-all bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid="sheet-shared-secret">
                    {cfg.shared_secret}
                  </div>
                )}
                <div className="mt-1">
                  This value MUST exactly match the <code>SHARED_SECRET</code> line at the top of your Apps Script. If you see "bad_secret" errors, click <strong>Show Setup</strong> and paste the correct value.
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isLeadership && <span className="text-xs text-slate-500 italic">Leadership-only</span>}
          {cfg.configured ? (
            <>
              <button
                onClick={bootstrap}
                disabled={!isLeadership || bootstrapping}
                className="btn-touch px-4 rounded-xl bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="sheet-bootstrap-btn"
                title="Rebuild the sheet from scratch from MongoDB data"
              >
                <RotateCw className="w-4 h-4" /> {bootstrapping ? "Syncing…" : "Push All Staff"}
              </button>
              <button
                onClick={() => setShowSetup(true)}
                disabled={!isLeadership}
                className="btn-touch px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold disabled:opacity-50"
              >
                Show Setup
              </button>
              <button
                onClick={disconnect}
                disabled={!isLeadership}
                className="btn-touch px-4 rounded-xl bg-white border border-rose-200 hover:bg-rose-50 text-rose-700 font-semibold disabled:opacity-50"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowSetup(true)}
              disabled={!isLeadership}
              className="btn-touch px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50"
              data-testid="sheet-connect-btn"
            >
              Connect Sheet
            </button>
          )}
        </div>
      </div>

      {showSetup && script && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="sheet-setup-modal">
          <div className="bg-white rounded-3xl max-w-3xl w-full overflow-hidden my-8">
            <div className="px-6 py-4 border-b border-emerald-100 bg-emerald-50 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-bold">Google Sheet</div>
                <h3 className="text-xl font-bold text-slate-900">Two-Way Live Payroll Sync — Setup</h3>
                <p className="text-sm text-slate-600 mt-1">Follow these 6 steps once. Takes about 5 minutes.</p>
              </div>
              <button onClick={() => setShowSetup(false)} className="p-1 rounded hover:bg-emerald-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">

              <Step n={1} title="Open your Google Sheet">
                <p>Open the Google Sheet you want to use. If you don't have one yet, <a className="text-emerald-700 underline" href="https://docs.google.com/spreadsheets/u/0/create" target="_blank" rel="noreferrer">create a blank sheet</a>. The script will build the payroll layout automatically.</p>
              </Step>

              <Step n={2} title="Open Apps Script">
                <p>In the sheet, click <strong>Extensions → Apps Script</strong>. A new browser tab will open with a code editor.</p>
              </Step>

              <Step n={3} title="Paste this entire script">
                <p className="text-xs text-slate-500 mb-2">The backend URL and shared secret are already filled in for you. Click <strong>Copy entire script</strong> below, then paste into Apps Script.</p>
                <div className="relative">
                  <pre className="bg-slate-900 text-emerald-100 text-[11px] leading-relaxed rounded-xl p-4 overflow-auto max-h-96 font-mono whitespace-pre">
{script.code}
                  </pre>
                  <button
                    onClick={() => copy(script.code, "Script")}
                    className="absolute top-2 right-2 px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold inline-flex items-center gap-1 shadow-lg"
                    data-testid="copy-script-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy entire script
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">In the Apps Script editor, select-all (Cmd/Ctrl+A) and delete the placeholder code first, then paste this. Save with Cmd/Ctrl+S.</p>
              </Step>

              <Step n={4} title="Install the onEdit trigger">
                <p>In the Apps Script editor, click the function dropdown next to ▶︎ Run, choose <code>installTrigger</code>, click <strong>Run</strong>. Google will prompt to authorize — review and approve (it's your own script editing your own sheet).</p>
              </Step>

              <Step n={5} title="Deploy as Web App">
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Click <strong>Deploy → New deployment</strong></li>
                  <li>Type: <strong>Web app</strong></li>
                  <li>Description: "RRDC payroll sync"</li>
                  <li>Execute as: <strong>Me</strong></li>
                  <li>Who has access: <strong>Anyone</strong></li>
                  <li>Click <strong>Deploy</strong>, copy the Web app URL.</li>
                </ol>
              </Step>

              <Step n={6} title="Paste the deploy URL below">
                <input
                  value={webappUrl}
                  onChange={(e) => setWebappUrl(e.target.value)}
                  placeholder="https://script.google.com/macros/s/AKfycb.../exec"
                  className="w-full px-4 py-3 rounded-xl border-2 border-emerald-200 outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-sm"
                  data-testid="sheet-webapp-url-input"
                />
                <details className="mt-2 text-xs text-slate-600">
                  <summary className="cursor-pointer font-semibold">Advanced: override the shared secret</summary>
                  <p className="mt-2">If you copy-pasted a script from somewhere else (e.g. chat) instead of using the Copy button above, the secret in your script may differ from ours. Paste the <code>SHARED_SECRET</code> value from the top of your script here so both sides match:</p>
                  <input
                    value={secretOverride}
                    onChange={(e) => setSecretOverride(e.target.value)}
                    placeholder="Leave blank to use the generated secret"
                    className="w-full mt-2 px-3 py-2 rounded-lg border border-slate-200 outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-xs"
                    data-testid="sheet-secret-override-input"
                  />
                  <p className="mt-2">Current generated secret: <code className="font-mono break-all">{script.shared_secret}</code></p>
                </details>
              </Step>

              <div className="grid grid-cols-2 gap-2 pt-2">
                <button onClick={() => setShowSetup(false)} className="rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold py-3">
                  Cancel
                </button>
                <button
                  onClick={saveUrl}
                  disabled={saving || !webappUrl}
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 disabled:opacity-50"
                  data-testid="sheet-save-config-btn"
                >
                  {saving ? "Connecting…" : "Connect Sheet"}
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="border-l-4 border-emerald-400 pl-4 py-1">
      <div className="text-xs uppercase tracking-wide text-emerald-700 font-bold">Step {n}</div>
      <h4 className="font-bold text-slate-900 mb-2">{title}</h4>
      <div className="text-sm text-slate-700 space-y-2">{children}</div>
    </div>
  );
}
